// IE Hub - Form-specific Theme Tokens
// Default styling for form components and field types

export interface FieldTokens {
  marginBottom: number;
  fullWidth: boolean;
  variant: string;
}

export interface SizeTokens {
  height: number;
  fontSize: string;
  padding: string;
}

export interface StateTokens {
  borderColor?: string;
  backgroundColor?: string;
  color?: string;
  cursor?: string;
  borderStyle?: string;
}

export interface InputTypeTokens {
  inputType?: string;
  multiline?: boolean;
  autoComplete?: string;
  inputProps?: {
    step?: string;
  };
  rows?: number;
  maxRows?: number;
}

export interface SelectTokens {
  variant: string;
  displayEmpty: boolean;
  native: boolean;
  MenuProps: {
    PaperProps: {
      elevation: number;
      sx: {
        maxHeight: number;
        marginTop: number;
      };
    };
  };
}

export interface ComponentTokens {
  size: string;
  color: string;
}

export interface DatePickerTokens {
  format: string;
  views: string[];
  openTo: string;
}

export interface ValidationTokens {
  helperText: {
    fontSize: string;
    marginTop: number;
    lineHeight: number;
  };
  required: {
    symbol: string;
    color: string;
    fontSize: string;
  };
  success: {
    color: string;
    icon: string;
  };
}

export interface LayoutSectionTokens {
  marginBottom: number;
  padding: number;
  borderRadius: number;
  backgroundColor: string;
  border: string;
}

export interface LayoutGroupTokens {
  marginBottom: number;
  gap: number;
}

export interface LayoutInlineTokens {
  display: string;
  gap: number;
  alignItems: string;
  flexWrap: string;
}

export interface LayoutGridTokens {
  container: {
    spacing: number;
  };
  item: {
    xs: number;
    sm: number;
    md: number;
  };
}

export interface LayoutTokens {
  section: LayoutSectionTokens;
  group: LayoutGroupTokens;
  inline: LayoutInlineTokens;
  grid: LayoutGridTokens;
}

export interface ActionContainerTokens {
  marginTop: number;
  display: string;
  gap: number;
  justifyContent: string;
  alignItems: string;
}

export interface ActionButtonTokens {
  variant: string;
  color: string;
  size: string;
}

export interface ActionsTokens {
  container: ActionContainerTokens;
  primary: ActionButtonTokens;
  secondary: ActionButtonTokens;
  cancel: ActionButtonTokens;
}

export interface FormTokens {
  field: FieldTokens;
  sizes: {
    small: SizeTokens;
    medium: SizeTokens;
    large: SizeTokens;
  };
  states: {
    error: StateTokens;
    success: StateTokens;
    warning: StateTokens;
    disabled: StateTokens;
    readonly: StateTokens;
  };
  types: {
    text: InputTypeTokens;
    email: InputTypeTokens;
    password: InputTypeTokens;
    number: InputTypeTokens;
    tel: InputTypeTokens;
    url: InputTypeTokens;
    search: InputTypeTokens;
    textarea: InputTypeTokens;
  };
  select: SelectTokens;
  checkbox: ComponentTokens;
  radio: ComponentTokens;
  datePicker: DatePickerTokens;
  validation: ValidationTokens;
  layout: LayoutTokens;
  actions: ActionsTokens;
}

export const formTokens: FormTokens = {
  // Field spacing and layout
  field: {
    marginBottom: 16,
    fullWidth: true,
    variant: 'outlined'
  },
  
  // Field sizes
  sizes: {
    small: {
      height: 40,
      fontSize: '0.875rem',
      padding: '8px 12px'
    },
    medium: {
      height: 48,
      fontSize: '1rem',
      padding: '12px 16px'
    },
    large: {
      height: 56,
      fontSize: '1.125rem',
      padding: '16px 20px'
    }
  },
  
  // Field states
  states: {
    error: {
      borderColor: '#d32f2f',
      backgroundColor: 'rgba(211, 47, 47, 0.05)'
    },
    success: {
      borderColor: '#2e7d32',
      backgroundColor: 'rgba(46, 125, 50, 0.05)'
    },
    warning: {
      borderColor: '#ed6c02',
      backgroundColor: 'rgba(237, 108, 2, 0.05)'
    },
    disabled: {
      backgroundColor: '#f5f5f5',
      color: '#9e9e9e',
      cursor: 'not-allowed'
    },
    readonly: {
      backgroundColor: '#fafafa',
      borderStyle: 'dashed'
    }
  },
  
  // Input types
  types: {
    text: {
      inputType: 'text',
      multiline: false,
      autoComplete: 'off'
    },
    email: {
      inputType: 'email',
      autoComplete: 'email'
    },
    password: {
      inputType: 'password',
      autoComplete: 'current-password'
    },
    number: {
      inputType: 'number',
      inputProps: {
        step: 'any'
      }
    },
    tel: {
      inputType: 'tel',
      autoComplete: 'tel'
    },
    url: {
      inputType: 'url',
      autoComplete: 'url'
    },
    search: {
      inputType: 'search'
    },
    textarea: {
      multiline: true,
      rows: 4,
      maxRows: 8
    }
  },
  
  // Select components
  select: {
    variant: 'outlined',
    displayEmpty: true,
    native: false,
    MenuProps: {
      PaperProps: {
        elevation: 8,
        sx: {
          maxHeight: 300,
          marginTop: 1
        }
      }
    }
  },
  
  // Checkbox and radio
  checkbox: {
    size: 'medium',
    color: 'primary'
  },
  
  radio: {
    size: 'medium',
    color: 'primary'
  },
  
  // Date picker
  datePicker: {
    format: 'MM/dd/yyyy',
    views: ['year', 'month', 'day'],
    openTo: 'day'
  },
  
  // Validation
  validation: {
    // Error message styling
    helperText: {
      fontSize: '0.75rem',
      marginTop: 4,
      lineHeight: 1.33
    },
    
    // Required field indicator
    required: {
      symbol: '*',
      color: '#d32f2f',
      fontSize: '1rem'
    },
    
    // Success indicator
    success: {
      color: '#2e7d32',
      icon: 'check_circle'
    }
  },
  
  // Layout and grouping
  layout: {
    // Form sections
    section: {
      marginBottom: 32,
      padding: 24,
      borderRadius: 8,
      backgroundColor: '#fafafa',
      border: '1px solid #e0e0e0'
    },
    
    // Field groups
    group: {
      marginBottom: 24,
      gap: 16
    },
    
    // Inline fields
    inline: {
      display: 'flex',
      gap: 16,
      alignItems: 'flex-start',
      flexWrap: 'wrap'
    },
    
    // Grid layout
    grid: {
      container: {
        spacing: 2
      },
      item: {
        xs: 12,
        sm: 6,
        md: 4
      }
    }
  },
  
  // Actions (buttons)
  actions: {
    container: {
      marginTop: 32,
      display: 'flex',
      gap: 16,
      justifyContent: 'flex-end',
      alignItems: 'center'
    },
    
    primary: {
      variant: 'contained',
      color: 'primary',
      size: 'large'
    },
    
    secondary: {
      variant: 'outlined',
      color: 'primary',
      size: 'large'
    },
    
    cancel: {
      variant: 'text',
      color: 'inherit',
      size: 'large'
    }
  }
};
